import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "HOME", path: "/" },
    { name: "BOOKINGS", path: "/bookings" },
    { name: "GALLERY", path: "/gallery" },
    { name: "FACILITIES AND FAQS", path: "/facilities-and-faqs" },
    { name: "CONTACT US", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
          : 'bg-white/90 backdrop-blur-md shadow-md'
      }`} 
      data-testid="header"
    >
      {/* Top gradient bar */}
      <div className="h-1 bg-gradient-to-r from-[#E67E22] via-[#F39C12] to-[#E67E22] bg-[length:200%_100%] animate-shimmer"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group" 
            data-testid="logo-link"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
              <img 
                src="/assets/logo.png" 
                alt="Wyldwoods Rural Retreat" 
                className="h-16 relative z-10 transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-semibold tracking-wide transition-all duration-300 rounded-lg group ${
                  isActive(link.path)
                    ? "text-white"
                    : "text-[#2C3E50] hover:text-[#E67E22]"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isActive(link.path) && (
                  <span className="absolute inset-0 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-lg"></span>
                )}
                <span className="relative z-10">{link.name}</span>
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#E67E22] to-[#F39C12] transition-all duration-300 ${
                  !isActive(link.path) && 'group-hover:w-full'
                }`}></span>
              </Link>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2.5 rounded-lg transition-all duration-300 ${
              isMenuOpen 
                ? 'bg-gradient-to-r from-[#E67E22] to-[#F39C12] text-white' 
                : 'bg-gray-100 text-[#2C3E50] hover:bg-gray-200'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="py-4 space-y-2" data-testid="mobile-nav">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 px-4 text-sm font-semibold tracking-wide rounded-lg transition-all duration-300 ${
                  isActive(link.path)
                    ? "bg-gradient-to-r from-[#E67E22] to-[#F39C12] text-white shadow-lg transform scale-105"
                    : "text-[#2C3E50] hover:bg-[#FEF3E2] hover:text-[#E67E22] hover:translate-x-2"
                } animate-fade-in-up`}
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;